import { useEffect, useState } from "react";
import { Save, FileText } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";

const contentFields = [
  {
    key: "homepage_intro",
    label: "Homepage Intro",
    description: "The welcome text shown on the homepage",
    rows: 3,
  },
  {
    key: "about_bio",
    label: "About Page Bio",
    description: "Main biography text on the About page",
    rows: 5,
  },
  {
    key: "about_achievements",
    label: "Achievements",
    description: "List of achievements, separated by | (pipe character)",
    rows: 4,
  },
  {
    key: "footer_disclaimer",
    label: "Footer Disclaimer",
    description: "Legal disclaimer shown in the footer and About page",
    rows: 4,
  },
];

export default function AdminContent() {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const data = await api.getAllContent();
        setContent(data);
      } catch {
        toast.error("Failed to load content");
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (key) => {
    setSaving({ ...saving, [key]: true });

    try {
      await api.updateContent(key, content[key] || "");
      toast.success("Content saved successfully");
    } catch {
      toast.error("Failed to save content");
    } finally {
      setSaving({ ...saving, [key]: false });
    }
  };

  const handleChange = (key, value) => {
    setContent({ ...content, [key]: value });
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="font-mono text-white/50 animate-pulse">Loading content...</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-content-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-russo text-3xl text-white uppercase tracking-tighter">
          PAGE CONTENT
        </h1>
        <p className="font-mono text-white/50 mt-2">
          Edit text content displayed throughout the site
        </p>
      </div>

      {/* Content Fields */}
      <div className="space-y-8">
        {contentFields.map((field) => (
          <div
            key={field.key}
            className="bg-demolition-paper border border-white/10 p-6"
            data-testid={`content-field-${field.key}`}
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-demolition-red" />
                <div>
                  <Label className="font-teko text-xl text-white uppercase tracking-wider">
                    {field.label}
                  </Label>
                  <p className="font-mono text-white/40 text-sm mt-1">
                    {field.description}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleSave(field.key)}
                disabled={saving[field.key]}
                className="flex items-center gap-2 px-4 py-2 bg-demolition-red text-white font-teko text-base uppercase tracking-wider hover:bg-demolition-red-hover transition-colors disabled:opacity-50"
                data-testid={`save-${field.key}-btn`}
              >
                <Save className="w-4 h-4" />
                {saving[field.key] ? "Saving..." : "Save"}
              </button>
            </div>

            <Textarea
              value={content[field.key] || ""}
              onChange={(e) => handleChange(field.key, e.target.value)}
              rows={field.rows}
              placeholder={`Enter ${field.label.toLowerCase()}...`}
              className="bg-demolition-subtle border-white/10 text-white font-mono resize-none"
              data-testid={`input-${field.key}`}
            />
          </div>
        ))}
      </div>

      {/* Tips */}
      <div className="mt-8 bg-demolition-subtle border border-white/10 p-6">
        <h3 className="font-teko text-lg text-demolition-chrome uppercase tracking-widest mb-4">
          TIPS
        </h3>
        <ul className="space-y-2 font-mono text-white/50 text-sm">
          <li>• Changes are saved per field - click Save after editing each section</li>
          <li>• For achievements, separate each item with a | (pipe) character</li>
          <li>• Keep the disclaimer text professional and legally appropriate</li>
          <li>• Changes will appear on the site immediately after saving</li>
        </ul>
      </div>
    </div>
  );
}
